import React, { useEffect, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getChampionById } from "constants/gameDefaults";
import app from "../firebaseConfig";

const functions = getFunctions(app);
const ADMIN_UID = "kq78ZkpEAFdw4eq8PLcnRFjBiP92";

const styles: Record<string, React.CSSProperties> = {
  page: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "#0a0a0f",
    overflowY: "auto",
    zIndex: 9999,
  },
  container: {
    padding: "2rem",
    maxWidth: "900px",
    margin: "0 auto",
    color: "#fff",
    fontFamily: "Montserrat, sans-serif",
  },
  title: {
    fontSize: "2rem",
    fontWeight: 800,
    marginBottom: "1.5rem",
    fontFamily: "Work Sans, sans-serif",
    letterSpacing: "2px",
  },
  loadingText: { fontSize: "1.2rem", opacity: 0.6 },
  errorText: { fontSize: "1.2rem", color: "#ff6b6b" },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "16px",
    marginBottom: "2rem",
  },
  card: {
    background: "rgba(255,255,255,0.06)",
    borderRadius: "12px",
    padding: "20px",
    textAlign: "center" as const,
    border: "1px solid rgba(255,255,255,0.08)",
  },
  cardValue: {
    fontSize: "2.2rem",
    fontWeight: 800,
    fontFamily: "Work Sans, sans-serif",
  },
  cardLabel: {
    fontSize: "0.85rem",
    opacity: 0.5,
    marginTop: "4px",
    textTransform: "uppercase" as const,
    letterSpacing: "1px",
  },
  sectionTitle: {
    fontSize: "1.2rem",
    fontWeight: 700,
    marginTop: "2rem",
    marginBottom: "1rem",
    opacity: 0.8,
  },
  chart: {
    width: "100%",
    borderRadius: "8px",
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.08)",
    marginBottom: "1.5rem",
  },
  barRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "6px 16px",
    borderTop: "1px solid rgba(255,255,255,0.04)",
    fontSize: "0.85rem",
  },
  barLabel: { width: "90px", opacity: 0.7, flexShrink: 0 },
  barContainer: { flex: 1 },
  bar: {
    height: "20px",
    borderRadius: "4px",
    minWidth: "2px",
    transition: "width 0.3s",
  },
  barValue: { width: "40px", textAlign: "right" as const, flexShrink: 0 },
};

interface SimRecord {
  id: string;
  timestampMs: number | null;
  uid: string;
  email: string | null;
  blueTeam: string[];
  redTeam: string[];
  winProbability: number | null;
}

interface AdminStats {
  totalUsers: number;
  totalGames: number;
  totalSimulations: number;
  totalPremium: number;
  visitorsPerDay: Record<string, number>;
  gamesByDay: Record<string, number>;
  simulationsByDay: Record<string, number>;
}

const champName = (id: string) => getChampionById(id)?.name || id;

function BarChart({
  data,
  color,
}: {
  data: Record<string, number>;
  color: string;
}) {
  const sorted = Object.entries(data).sort(([a], [b]) => a.localeCompare(b));
  const max = Math.max(...sorted.map(([, v]) => v), 1);
  if (sorted.length === 0)
    return <p style={{ padding: "16px", opacity: 0.5 }}>No data yet</p>;
  return (
    <div style={styles.chart}>
      {sorted.map(([date, count]) => (
        <div key={date} style={styles.barRow}>
          <span style={styles.barLabel}>{date.slice(5)}</span>
          <div style={styles.barContainer}>
            <div
              style={{
                ...styles.bar,
                background: color,
                width: `${(count / max) * 100}%`,
              }}
            />
          </div>
          <span style={styles.barValue}>{count}</span>
        </div>
      ))}
    </div>
  );
}

function fmtTime(ms: number | null) {
  if (!ms) return "—";
  return new Date(ms).toISOString().replace("T", " ").slice(5, 16);
}

function SimulationLog({ sims }: { sims: SimRecord[] }) {
  if (sims.length === 0)
    return <p style={{ padding: "16px", opacity: 0.5 }}>No simulations yet.</p>;

  // Per-user tally — a single uid/email dominating the list = likely a bot/loop
  const byUser: Record<string, number> = {};
  sims.forEach((s) => {
    const key = s.email || s.uid || "anonymous";
    byUser[key] = (byUser[key] || 0) + 1;
  });
  const ranked = Object.entries(byUser).sort(([, a], [, b]) => b - a);

  return (
    <>
      <h2 style={styles.sectionTitle}>By User (last {sims.length})</h2>
      <div style={styles.chart}>
        {ranked.map(([who, count]) => (
          <div key={who} style={styles.barRow}>
            <span style={{ flex: 1, opacity: 0.8, fontSize: "0.85rem" }}>
              {who}
            </span>
            <span style={styles.barValue}>{count}</span>
          </div>
        ))}
      </div>

      <h2 style={styles.sectionTitle}>Recent Simulations</h2>
      <div style={styles.chart}>
        {sims.map((s) => (
          <div key={s.id} style={styles.barRow}>
            <span style={{ width: "110px", opacity: 0.6, flexShrink: 0 }}>
              {fmtTime(s.timestampMs)}
            </span>
            <span style={{ flex: 1, fontSize: "0.8rem" }}>
              <span style={{ color: "#4a90d9" }}>
                {(s.blueTeam || []).map(champName).join(", ")}
              </span>
              {"  vs  "}
              <span style={{ color: "#ff6b6b" }}>
                {(s.redTeam || []).map(champName).join(", ")}
              </span>
            </span>
            <span style={{ width: "50px", textAlign: "right", flexShrink: 0 }}>
              {s.winProbability != null
                ? `${Math.round(s.winProbability)}%`
                : "—"}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

function Admin() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [sims, setSims] = useState<SimRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [authState, setAuthState] = useState<"pending" | "admin" | "denied">(
    "pending",
  );

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser && firebaseUser.uid === ADMIN_UID) {
        setAuthState("admin");
      } else {
        setAuthState("denied");
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (authState !== "admin") return;
    const fetchStats = async () => {
      try {
        const getAdminStats = httpsCallable(functions, "getAdminStats");
        const getSimulationHistory = httpsCallable(
          functions,
          "getSimulationHistory",
        );
        const [statsRes, simsRes] = await Promise.all([
          getAdminStats({}),
          getSimulationHistory({ limit: 200 }),
        ]);
        setStats(statsRes.data as AdminStats);
        setSims((simsRes.data as { simulations: SimRecord[] }).simulations);
      } catch (err) {
        setError("Failed to load admin stats.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [authState]);

  const wrap = (content: React.ReactNode) => (
    <div style={styles.page}>
      <div style={styles.container}>{content}</div>
    </div>
  );

  if (authState === "pending")
    return wrap(
      <>
        <h1 style={styles.title}>DRAFTER ADMIN</h1>
        <p style={styles.loadingText}>Authenticating...</p>
      </>,
    );
  if (authState === "denied")
    return wrap(
      <>
        <h1 style={styles.title}>DRAFTER ADMIN</h1>
        <p style={styles.errorText}>Access denied.</p>
      </>,
    );
  if (loading)
    return wrap(
      <>
        <h1 style={styles.title}>DRAFTER ADMIN</h1>
        <p style={styles.loadingText}>Loading stats...</p>
      </>,
    );
  if (error || !stats)
    return wrap(
      <>
        <h1 style={styles.title}>DRAFTER ADMIN</h1>
        <p style={styles.errorText}>{error}</p>
      </>,
    );

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <h1 style={styles.title}>DRAFTER ADMIN</h1>

        <div style={styles.cardGrid}>
          <div style={styles.card}>
            <div style={styles.cardValue}>
              {stats.totalUsers.toLocaleString()}
            </div>
            <div style={styles.cardLabel}>Visitors (30d)</div>
          </div>
          <div style={styles.card}>
            <div style={styles.cardValue}>
              {stats.totalGames.toLocaleString()}
            </div>
            <div style={styles.cardLabel}>Matchmaking (30d)</div>
          </div>
          <div style={styles.card}>
            <div style={styles.cardValue}>
              {stats.totalSimulations.toLocaleString()}
            </div>
            <div style={styles.cardLabel}>Simulations (30d)</div>
          </div>
          <div style={styles.card}>
            <div style={{ ...styles.cardValue, color: "#ffd700" }}>
              {stats.totalPremium}
            </div>
            <div style={styles.cardLabel}>Premium Users</div>
          </div>
        </div>

        <h2 style={styles.sectionTitle}>Daily Visitors (30d)</h2>
        <BarChart data={stats.visitorsPerDay} color="#a78bfa" />

        <h2 style={styles.sectionTitle}>Matchmaking per Day (30d)</h2>
        <BarChart data={stats.gamesByDay} color="#4a90d9" />

        <h2 style={styles.sectionTitle}>Simulations per Day (30d)</h2>
        <BarChart data={stats.simulationsByDay} color="#f59e0b" />

        <SimulationLog sims={sims} />
      </div>
    </div>
  );
}

export default Admin;
